import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, Navigate } from "react-router-dom";
import { Layout, Typography, Button, Row, Col, Select, Carousel } from "antd";
import { SearchOutlined, ArrowRightOutlined } from "@ant-design/icons";
import classNames from "classnames";
import styles from "@/pages/country/CountryBlogDetail.module.scss";
import parse from "html-react-parser";

import { getBlogs } from "@/services/common/blogs";
import { getDisciplines } from "@/services/common/getDisciplines";
import { getCountryNameByIsoCode } from "@/helpers/countryList";

const { Title, Paragraph } = Typography;

const CountryBlog = () => {
  const { slug, id } = useParams();
  const blogId = Number(id);

  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [sections, setSections] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [disciplines, setDisciplines] = useState([]);
  const [selectedDiscipline, setSelectedDiscipline] = useState(null);
  const [loading, setLoading] = useState(true);

  // Ref for section carousel
  const sectionCarouselRef = useRef(null);

  // Fetch all blogs and pick out the one matching either `id` or `slug`
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const allBlogs = (await getBlogs()) ?? [];
        let found;
        found = allBlogs.find((b) => b.id === blogId && b.slug === slug && b.metadata?.countryCode);
        setPost(found ?? undefined);
        setSections(found?.contents ?? []);
      } catch (err) {
        console.error("Error fetching blogs:", err);
        setPost(undefined);
      } finally {
        setLoading(false);
      }
    })();
  }, [blogId, slug]);

  // Once we have a post, fetch the disciplines
  useEffect(() => {
    if (post) {
      getDisciplines()
        .then(setDisciplines)
        .catch((err) => console.error("Error fetching disciplines:", err));
    }
  }, [post]);

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        {/* <Spin size="large" /> */}
      </div>
    );
  }

  // If not found, navigate to 404
  if (!post) {
    return <Navigate to="/404" replace />;
  }

  const handleSectionClick = (idx) => {
    setSelectedIndex(idx);
    sectionCarouselRef.current?.goTo(idx);
  };

  const handleConnectMentor = () => {
    if (!selectedDiscipline) {
      const params = new URLSearchParams({
        countryCode: post.metadata.countryCode,
        countryName: post.title,
      });
      navigate(`/guides?${params.toString()}`);
      // If no discipline is selected, navigate to guides with country info
      return;
    }
    const discipline = disciplines.find((d) => d.id === selectedDiscipline);
    if (!discipline) return;
    const params = new URLSearchParams({
      disciplineId: String(selectedDiscipline),
      disciplineName: discipline.name,
      countryCode: post.metadata.countryCode,
      countryName: post.title,
    });
    navigate(`/guides?${params.toString()}`);
  };

  return (
    <Layout className={styles.blogLayout}>
      {/* Hero with Carousel */}
      <div className={styles.heroContainer}>
        <Carousel autoplay className={styles.heroCarousel} dots={false} arrows={false}>
          {post.coverImages.map((src, idx) => (
            <div key={idx} className={styles.heroSlide}>
              <img src={src} alt={`cover-${idx}`} className={styles.heroImage} />
            </div>
          ))}
        </Carousel>
        <div
          style={{
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            height: "100%",
            width: "100%",
            position: "absolute",
            inset: 0,
          }}
          className={styles.overlay}
        />
        <div className={styles.heroContent}>
          <div className={styles.heroContentText}>
            <div className={styles.wrapper}>
              <Title level={1}>{post.title}</Title>
              {post.subTitle && (
                <Paragraph className={styles.heroSubtitle}>{post.subTitle}</Paragraph>
              )}
            </div>
          </div>
          <div className={styles.cta}>
            <Button type="primary" size="large" className={styles.getInTouch}>
              <a href="/contact-us" target="_blank" rel="noopener noreferrer">
                Get in touch <ArrowRightOutlined />
              </a>
            </Button>
          </div>
        </div>
      </div>

      {/* Search & Connect */}
      <div className={styles.searchSection}>
        <Row justify="space-between" align="middle" style={{ width: "100%" }}>
          <Col xs={24} md={12}>
            <Select
              placeholder="What do you want to study?"
              suffixIcon={<SearchOutlined />}
              style={{ width: "100%" }}
              onChange={(value) => setSelectedDiscipline(Number(value))}
              options={disciplines.map((d) => ({ value: d.id, label: d.name }))}
            />
          </Col>
          <Col xs={24} md={12} className={styles.connectMentor}>
            <Button type="link" size="large" onClick={handleConnectMentor}>
              Connect with a mentor from {getCountryNameByIsoCode(post.metadata.countryCode)}{" "}
              <ArrowRightOutlined />
            </Button>
          </Col>
        </Row>
      </div>

      {/* Main Content with Fade Transition */}
      <div className={styles.mainContent}>
        <Row gutter={[32, 32]} style={{ margin: 0 }}>
          {/* Sidebar */}
          <Col xs={24} md={8} className={styles.sectionWrapper}>
            <div className={styles.sidebar}>
              <Title level={4} className={styles.inventoryTitle}>
                {post.contentTitle}
              </Title>
              <nav className={styles.sidebarNav}>
                {sections.map((sec, i) => (
                  <a
                    key={i}
                    href="#"
                    className={classNames(styles.sidebarLink, {
                      [styles.sidebarLinkActive]: selectedIndex === i,
                    })}
                    onClick={(e) => {
                      e.preventDefault();
                      handleSectionClick(i);
                    }}
                  >
                    {sec.sectionTitle}
                  </a>
                ))}
              </nav>
            </div>
          </Col>

          {/* Section Content Slider */}
          <Col xs={24} md={16} className={styles.sectionWrapper}>
            <Carousel
              ref={sectionCarouselRef}
              effect="fade"
              dots={false}
              arrows={false}
              beforeChange={(_, to) => setSelectedIndex(to)}
            >
              {sections.map((sec, i) => (
                <div key={i} className={styles.contentSlide}>
                  {parse(sec.sectionContent)}
                </div>
              ))}
            </Carousel>
          </Col>
        </Row>
      </div>
    </Layout>
  );
};

export default CountryBlog;
